import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import CommonService from '../../../services/common.service';
import { useCurrencyMaster } from '../../../hooks/useCurrencyMaster';
import { useProductDetailsMaster } from '../../../hooks/useProductDetailsMaster';
import { useFactoryDetailsMaster } from '../../../hooks/useFactoryDetailsMaster';
import '../../../assets/styles/approve_invoice_list.scss';

export default function ApproveInvoiceList() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const urlSearchHint =
    searchParams.get('q') || searchParams.get('po') || searchParams.get('search') || '';
  const [searchQuery, setSearchQuery] = useState(urlSearchHint);
  const [statusFilter, setStatusFilter] = useState('All Status');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isAcceptanceModalOpen, setIsAcceptanceModalOpen] = useState(false);
  
  // Master Data Hooks
  const { currencyOptions } = useCurrencyMaster();
  const { attributeUnitOptions } = useProductDetailsMaster();
  const { paymentScheduleOptions, shippingTermsOptions, modeOfTransportOptions } = useFactoryDetailsMaster();
  const [acceptanceForm, setAcceptanceForm] = useState({
    decision: '',
    acceptedBy: '',
    designation: '',
    acceptanceDate: new Date().toISOString().split('T')[0],
    paymentCommitmentDate: '',
    acceptanceNotes: '',
    disputeCategory: '',
    disputeReason: '',
    remarks: ''
  });

  useEffect(() => {
    if (urlSearchHint.trim()) {
      setSearchQuery(urlSearchHint.trim());
    }
  }, [urlSearchHint]);

  // Fetch invoices from backend
  useEffect(() => {
    const fetchInvoices = async () => {
      try {
        setLoading(true);
        const response = await CommonService.get('/seller-invoice/buyer/my-invoices');
        
        if (response && response.success && response.data) {
          // Transform API data to match UI structure
          const transformedInvoices = response.data.map(invoice => {
            const displayStatus = resolveInvoiceDisplayStatus(invoice);

            // Map trade_type to type
            let type = 'Inter-State';
            if (invoice.trade_type) {
              const tradeTypeLower = invoice.trade_type.toLowerCase();
              if (tradeTypeLower.includes('intra-state') || tradeTypeLower.includes('same state')) {
                type = 'Intra-State';
              } else if (tradeTypeLower.includes('export') || tradeTypeLower.includes('abroad')) {
                type = 'Export';
              } else if (tradeTypeLower.includes('inter-state')) {
                type = 'Inter-State';
              }
            }

            // Format amount with currency
            const currency = invoice.currency || 'INR';
            
            // Calculate amount: use grand_total if available, otherwise calculate from line items
            let amount = invoice.grand_total;
            if (!amount || amount === 0) {
              // Calculate from line items if grand_total is not available
              if (invoice.line_items && invoice.line_items.length > 0) {
                amount = invoice.line_items.reduce((sum, item) => {
                  return sum + (parseFloat(item.total_amount) || 0);
                }, 0);
              } else {
                // Fallback to subtotal + taxes if line items not available
                const subtotal = parseFloat(invoice.subtotal) || 0;
                const fobValue = parseFloat(invoice.fob_value) || subtotal;
                
                // Calculate taxes from line items if available
                let totalTax = 0;
                if (invoice.line_items && invoice.line_items.length > 0) {
                  invoice.line_items.forEach(item => {
                    totalTax += (parseFloat(item.cgst) || 0) + 
                               (parseFloat(item.sgst) || 0) + 
                               (parseFloat(item.igst) || 0) + 
                               (parseFloat(item.cess) || 0) + 
                               (parseFloat(item.customs_duty) || 0) + 
                               (parseFloat(item.tax) || 0);
                  });
                }
                
                amount = fobValue + totalTax;
              }
            }
            
            const formattedAmount = formatCurrency(amount || 0, currency);

            // Format payment terms
            // Priority: payment_terms label > payment_due_within
            let paymentTermsText = '';
            if (invoice.payment_terms) {
              // First try to get the label from payment_terms ID
              const paymentTermsLabel = getPaymentTermsLabel(invoice.payment_terms);
              if (paymentTermsLabel && paymentTermsLabel.trim() !== '') {
                paymentTermsText = paymentTermsLabel;
              } else if (invoice.payment_due_within) {
                // Fallback to payment_due_within if payment_terms label is not available
                paymentTermsText = `${invoice.payment_due_within} days from invoice date`;
              }
            } else if (invoice.payment_due_within) {
              // Use payment_due_within only if payment_terms is not available
              paymentTermsText = `${invoice.payment_due_within} days from invoice date`;
            }

            return {
              id: invoice.id,
              invoiceNumber: invoice.invoice_number || '',
              status: displayStatus,
              type: type,
              poNumber: invoice.po_reference || invoice.po_number || '',
              seller: invoice.seller_company_name || '',
              invoiceDate: invoice.invoice_date || '',
              paymentTerms: paymentTermsText,
              dueDate: invoice.due_date || '',
              amount: formattedAmount,
              currency: currency,
              buyerApprovalStatus: invoice.buyer_approval_status || 'not_required',
              buyerApprovalPending: invoice.buyer_approval_pending === true,
              canAccept: invoice.can_accept !== false,
              // Store full invoice data for modal
              fullInvoiceData: invoice
            };
          });
          
          setInvoices(transformedInvoices);
        } else {
          setInvoices([]);
        }
      } catch (error) {
        console.error('Error fetching invoices:', error);
        toast.error('Failed to fetch invoices');
        setInvoices([]);
      } finally {
        setLoading(false);
      }
    };

    fetchInvoices();
  }, []);

  // Update payment terms labels when paymentScheduleOptions are loaded
  useEffect(() => {
    if (paymentScheduleOptions && paymentScheduleOptions.length > 0 && invoices.length > 0) {
      // Re-process invoices to update payment terms labels
      setInvoices(prevInvoices => {
        const updatedInvoices = prevInvoices.map(invoice => {
          if (invoice.fullInvoiceData) {
            const invoiceData = invoice.fullInvoiceData;
            let paymentTermsText = '';
            
            // Priority: payment_terms label > payment_due_within
            if (invoiceData.payment_terms) {
              const paymentTermsLabel = getPaymentTermsLabel(invoiceData.payment_terms);
              if (paymentTermsLabel && paymentTermsLabel.trim() !== '' && paymentTermsLabel !== String(invoiceData.payment_terms)) {
                paymentTermsText = paymentTermsLabel;
              } else if (invoiceData.payment_due_within) {
                paymentTermsText = `${invoiceData.payment_due_within} days from invoice date`;
              } else {
                paymentTermsText = invoice.paymentTerms || String(invoiceData.payment_terms);
              }
            } else if (invoiceData.payment_due_within) {
              paymentTermsText = `${invoiceData.payment_due_within} days from invoice date`;
            } else {
              paymentTermsText = invoice.paymentTerms || '';
            }
            
            // Only update if payment terms changed
            if (paymentTermsText !== invoice.paymentTerms) {
              return {
                ...invoice,
                paymentTerms: paymentTermsText
              };
            }
          }
          return invoice;
        });
        
        // Only return new array if something changed
        const hasChanges = updatedInvoices.some((updated, index) => 
          updated.paymentTerms !== prevInvoices[index].paymentTerms
        );
        
        return hasChanges ? updatedInvoices : prevInvoices;
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paymentScheduleOptions]);

  // Helper function to get unit label from unit ID/value
  const getUnitLabel = (unitValue) => {
    if (!unitValue) return 'pcs';
    if (!attributeUnitOptions || attributeUnitOptions.length === 0) return unitValue;
    
    const unitValueNum = isNaN(unitValue) ? null : Number(unitValue);
    const foundByValue = attributeUnitOptions.find(opt => {
      if (String(opt.value) === String(unitValue)) return true;
      if (unitValueNum !== null && !isNaN(opt.value)) {
        return Number(opt.value) === unitValueNum;
      }
      return false;
    });
    
    if (foundByValue) {
      return foundByValue.label || unitValue;
    }
    
    const foundByLabel = attributeUnitOptions.find(opt => 
      opt.label === String(unitValue) || 
      opt.label?.toLowerCase() === String(unitValue).toLowerCase()
    );
    if (foundByLabel) {
      return foundByLabel.label;
    }
    
    return unitValue;
  };

  // Helper function to get currency code from currency ID/value
  const getCurrencyCode = (currencyValue) => {
    if (!currencyValue) return 'INR';
    if (!currencyOptions || currencyOptions.length === 0) {
      // Fallback: if it's already a currency code, return it
      const upperValue = String(currencyValue).toUpperCase();
      if (['INR', 'USD', 'EUR', 'GBP'].includes(upperValue)) {
        return upperValue;
      }
      return 'INR';
    }
    
    // Try to find by value (currency ID)
    const foundByValue = currencyOptions.find(opt => 
      String(opt.value) === String(currencyValue) ||
      String(opt.id) === String(currencyValue)
    );
    if (foundByValue) {
      return foundByValue.currencyCode || foundByValue.label || 'INR';
    }
    
    // Try to find by currency code
    const foundByCode = currencyOptions.find(opt => 
      opt.currencyCode === String(currencyValue).toUpperCase()
    );
    if (foundByCode) {
      return foundByCode.currencyCode || 'INR';
    }
    
    // Fallback: if it's already a currency code, return it
    const upperValue = String(currencyValue).toUpperCase();
    if (['INR', 'USD', 'EUR', 'GBP'].includes(upperValue)) {
      return upperValue;
    }
    
    return 'INR';
  };

  // Helper function to get currency symbol
  const getCurrencySymbol = (currencyCode) => {
    if (!currencyCode) return '';
    if (currencyOptions && currencyOptions.length > 0) {
      const foundByCode = currencyOptions.find(opt => 
        opt.currencyCode === String(currencyCode).toUpperCase() ||
        String(opt.value) === String(currencyCode) ||
        String(opt.id) === String(currencyCode)
      );
      if (foundByCode && foundByCode.currencySymbol) {
        return foundByCode.currencySymbol;
      }
    }
    
    // Fallback to hardcoded symbols
    const currencySymbols = {
      'INR': '₹',
      'USD': '$',
      'EUR': '€',
      'GBP': '£'
    };
    return currencySymbols[currencyCode?.toUpperCase()] || '';
  };

  // Format currency helper
  const formatCurrency = (amount, currencyValue) => {
    if (!amount && amount !== 0) return 'N/A';
    
    const numAmount = parseFloat(amount);
    if (isNaN(numAmount)) return 'N/A';
    
    // Get currency code from currency value (which might be an ID)
    const currencyCode = getCurrencyCode(currencyValue);
    const symbol = getCurrencySymbol(currencyCode);
    
    const formatted = numAmount.toLocaleString('en-IN', {
      minimumFractionDigits: 2, 
      maximumFractionDigits: 2 
    });
    
    return `${symbol} ${formatted}`;
  };

  // Helper function to get payment terms label from payment terms ID/value
  const getPaymentTermsLabel = (paymentTermsValue) => {
    if (!paymentTermsValue) return '';
    if (!paymentScheduleOptions || paymentScheduleOptions.length === 0) {
      // If it's already a label, return it
      return String(paymentTermsValue);
    }
    
    // Try to find by value (payment terms ID)
    const foundByValue = paymentScheduleOptions.find(opt => 
      String(opt.value) === String(paymentTermsValue) ||
      String(opt.id) === String(paymentTermsValue)
    );
    if (foundByValue) {
      return foundByValue.label || String(paymentTermsValue);
    }
    
    // Try to find by label (case-insensitive)
    const foundByLabel = paymentScheduleOptions.find(opt => 
      opt.label === String(paymentTermsValue) ||
      opt.label?.toLowerCase() === String(paymentTermsValue).toLowerCase() ||
      opt.label?.toLowerCase().includes(String(paymentTermsValue).toLowerCase())
    );
    if (foundByLabel) {
      return foundByLabel.label;
    }
    
    // Fallback: return the original value
    return String(paymentTermsValue);
  };

  // Helper function to get delivery terms (incoterms) label from delivery terms ID/value
  const getDeliveryTermsLabel = (deliveryTermsValue) => {
    if (!deliveryTermsValue) return '';
    if (!shippingTermsOptions || shippingTermsOptions.length === 0) {
      // If it's already a label, return it
      return String(deliveryTermsValue);
    }
    
    // Try to find by value (delivery terms ID)
    const foundByValue = shippingTermsOptions.find(opt => 
      String(opt.value) === String(deliveryTermsValue) ||
      String(opt.id) === String(deliveryTermsValue)
    );
    if (foundByValue) {
      return foundByValue.label || String(deliveryTermsValue);
    }
    
    // Try to find by label (case-insensitive)
    const foundByLabel = shippingTermsOptions.find(opt => 
      opt.label === String(deliveryTermsValue) ||
      opt.label?.toLowerCase() === String(deliveryTermsValue).toLowerCase() ||
      opt.label?.toLowerCase().includes(String(deliveryTermsValue).toLowerCase())
    );
    if (foundByLabel) {
      return foundByLabel.label;
    }
    
    // Fallback: return the original value
    return String(deliveryTermsValue);
  };

  // Helper function to get shipping method label from shipping method ID/value
  const getShippingMethodLabel = (shippingMethodValue) => {
    if (!shippingMethodValue) return '';
    if (!modeOfTransportOptions || modeOfTransportOptions.length === 0) {
      // If it's already a label, return it
      return String(shippingMethodValue);
    }
    
    // Try to find by value (shipping method ID)
    const foundByValue = modeOfTransportOptions.find(opt => 
      String(opt.value) === String(shippingMethodValue) ||
      String(opt.id) === String(shippingMethodValue)
    );
    if (foundByValue) {
      return foundByValue.label || String(shippingMethodValue);
    }
    
    // Try to find by label (case-insensitive)
    const foundByLabel = modeOfTransportOptions.find(opt => 
      opt.label === String(shippingMethodValue) ||
      opt.label?.toLowerCase() === String(shippingMethodValue).toLowerCase() ||
      opt.label?.toLowerCase().includes(String(shippingMethodValue).toLowerCase())
    );
    if (foundByLabel) {
      return foundByLabel.label;
    }
    
    // Fallback: return the original value
    return String(shippingMethodValue);
  };

  // Sample invoice data (removed - now using API data)
  const sampleInvoices = [
    {
      id: 1,
      invoiceNumber: 'INV-2025-001234',
      status: 'Pending Acceptance',
      type: 'Inter-State',
      poNumber: 'PO-2025-001234',
      seller: 'ABC Manufacturing Ltd.',
      invoiceDate: '2025-12-01',
      paymentTerms: '30 days from invoice date',
      dueDate: '2025-12-31',
      amount: 'INR 2,91,550.00',
      currency: 'INR'
    },
    {
      id: 2,
      invoiceNumber: 'INV-2025-002345',
      status: 'Accepted',
      type: 'Export',
      poNumber: 'PO-2025-002345',
      seller: 'Indian Exports Pvt Ltd',
      invoiceDate: '2025-11-28',
      paymentTerms: 'LC at sight',
      dueDate: '2025-12-28',
      amount: 'USD 38,000.00',
      currency: 'USD'
    },
    {
      id: 3,
      invoiceNumber: 'INV-2025-003456',
      status: 'Overdue',
      type: 'Intra-State',
      poNumber: 'PO-2025-003456',
      seller: 'Karnataka Steel Works',
      invoiceDate: '2025-11-25',
      paymentTerms: '5 days from invoice',
      dueDate: '2025-11-30',
      amount: 'INR 56,640.00',
      currency: 'INR'
    }
  ];

  const statusOptions = [
    'All Status',
    'Pending Buyer Approval',
    'Approved',
    'Pending Acceptance',
    'Approval Rejected',
    'Accepted',
    'Overdue',
  ];

  const resolveInvoiceDisplayStatus = (invoice) => {
    if (invoice.status === 'Accepted') return 'Accepted';
    const approval = invoice.buyer_approval_status || 'not_required';
    if (approval === 'pending') return 'Pending Buyer Approval';
    if (approval === 'rejected') return 'Approval Rejected';
    if (approval === 'approved') return 'Approved';
    if (invoice.status === 'Generated' || invoice.status === 'Sent') {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const dueDate = invoice.due_date ? new Date(invoice.due_date) : null;
      if (dueDate) {
        dueDate.setHours(0, 0, 0, 0);
        if (dueDate < today) return 'Overdue';
      }
      return 'Pending Acceptance';
    }
    return 'Pending Acceptance';
  };

  const canShowAcceptInvoice = (invoice) =>
    invoice.canAccept !== false &&
    ['Approved', 'Pending Acceptance', 'Overdue'].includes(invoice.status);

  const formatDate = (dateString) => {
    if (!dateString) return '—';
    const date = new Date(dateString);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const getStatusClass = (status) => {
    const statusMap = {
      'Pending Acceptance': 'ail-status-pending',
      'Pending Buyer Approval': 'ail-status-approval-pending',
      'Approved': 'ail-status-buyer-approved',
      'Approval Rejected': 'ail-status-approval-rejected',
      'Accepted': 'ail-status-accepted',
      'Overdue': 'ail-status-overdue'
    };
    return statusMap[status] || 'ail-status-pending';
  };

  const getTypeClass = (type) => {
    const typeMap = {
      'Inter-State': 'ail-type-inter-state',
      'Intra-State': 'ail-type-intra-state',
      'Export': 'ail-type-export'
    };
    return typeMap[type] || 'ail-type-default';
  };

  const filteredInvoices = invoices.filter(invoice => {
    const normalizedQuery = searchQuery.trim().toLowerCase();
    const searchFields = [
      invoice.invoiceNumber,
      invoice.poNumber,
      invoice.seller
    ].filter(Boolean);

    const matchesSearch = normalizedQuery === '' 
      ? true 
      : searchFields.some(field => field.toLowerCase().includes(normalizedQuery));
    
    const matchesStatus = statusFilter === 'All Status' || invoice.status === statusFilter;
    
    return matchesSearch && matchesStatus;
  });

  // Get detailed invoice data from API
  const getInvoiceDetails = async (invoiceId) => {
    try {
      const response = await CommonService.get(`/seller-invoice/${invoiceId}`);
      
      if (response && response.success && response.data) {
        const invoice = response.data;
        
        // Transform to match modal structure
        const currencyValue = invoice.currency || 'INR';
        const currencyCode = getCurrencyCode(currencyValue); // Convert ID to code
        const grandTotal = invoice.grand_total || 0;
        const formattedAmount = formatCurrency(grandTotal, currencyValue);
        
        // Get trade type from invoice
        const tradeType = invoice.trade_type || '';
        const tradeTypeLower = (tradeType || '').toLowerCase();
        
        // Determine trade type flags based on tradeType
        const isIntraState = tradeTypeLower.includes('intra-state') || 
                            (tradeTypeLower.includes('cgst') && tradeTypeLower.includes('sgst'));
        const isImport = tradeTypeLower.includes('import') || 
                        tradeTypeLower.includes('cif') || 
                        tradeTypeLower.includes('bcd');
        const isThirdCountry = tradeTypeLower.includes('third country');
        const isExport = tradeTypeLower.includes('export') || 
                        tradeTypeLower.includes('lut');
        const isInterState = tradeTypeLower.includes('inter-state') || 
                            (tradeTypeLower.includes('igst') && !isExport && !isImport && !isIntraState);
        
        const transformedInvoice = {
          id: invoice.id,
          invoiceNumber: invoice.invoice_number || '',
          invoiceDate: invoice.invoice_date || '',
          dueDate: invoice.due_date || '',
          poNumber: invoice.po_reference || invoice.po_number || '',
          currency: currencyCode, // Store currency code instead of ID
          currencyValue: currencyValue, // Keep original value for reference
          amount: formattedAmount,
          status: resolveInvoiceDisplayStatus(invoice),
          buyerApprovalStatus: invoice.buyer_approval_status || 'not_required',
          buyerApprovalPending: invoice.buyer_approval_pending === true,
          canAccept: invoice.can_accept !== false,
          buyerApproval: invoice.buyer_approval || null,
          tradeType: tradeType,
          isIntraState: isIntraState,
          isImport: isImport,
          isThirdCountry: isThirdCountry,
          isExport: isExport,
          isInterState: isInterState,
          seller: {
            companyName: invoice.seller_company_name || '',
            gstin: invoice.seller_gstin || '',
            address: Array.isArray(invoice.seller_address) 
              ? invoice.seller_address.join(', ') 
              : (typeof invoice.seller_address === 'string' ? invoice.seller_address : ''),
            email: invoice.seller_email || '',
            phone: invoice.seller_phone || ''
          },
          buyer: {
            companyName: invoice.buyer_company_name || '',
            gstin: invoice.buyer_tax_id || 'N/A (International)',
            address: Array.isArray(invoice.buyer_address) 
              ? invoice.buyer_address.join(', ') 
              : (typeof invoice.buyer_address === 'string' ? invoice.buyer_address : '')
          },
          items: (invoice.line_items || []).map(item => {
            const currencyValue = invoice.currency || 'INR';
            const currencyCode = getCurrencyCode(currencyValue);
            const symbol = getCurrencySymbol(currencyCode);
            
            const unitPrice = parseFloat(item.unit_price) || 0;
            const quantity = parseFloat(item.quantity) || 0;
            const discount = parseFloat(item.discount) || 0;
            
            // Calculate item subtotal and taxable amount
            const itemSubtotal = quantity * unitPrice;
            const discountAmount = itemSubtotal * (discount / 100);
            const taxableAmount = itemSubtotal - discountAmount;
            const totalAmount = parseFloat(item.total_amount) || 0;
            
            // Tax values are stored as percentages - calculate tax amounts
            let cgst = 0;
            let sgst = 0;
            let igst = 0;
            let cess = 0;
            let customsDuty = 0;
            let tax = 0;
            
            if (isIntraState) {
              const cgstPercent = parseFloat(item.cgst) || 0;
              const sgstPercent = parseFloat(item.sgst) || 0;
              const cessPercent = parseFloat(item.cess) || 0;
              cgst = taxableAmount * (cgstPercent / 100);
              sgst = taxableAmount * (sgstPercent / 100);
              cess = taxableAmount * (cessPercent / 100);
            } else if (isImport) {
              const customsDutyPercent = parseFloat(item.customs_duty) || 0;
              const igstPercent = parseFloat(item.igst) || 0;
              const cessPercent = parseFloat(item.cess) || 0;
              customsDuty = taxableAmount * (customsDutyPercent / 100);
              const amountAfterCustoms = taxableAmount + customsDuty;
              igst = amountAfterCustoms * (igstPercent / 100);
              cess = amountAfterCustoms * (cessPercent / 100);
            } else if (isThirdCountry) {
              const taxPercent = parseFloat(item.tax) || 0;
              tax = taxableAmount * (taxPercent / 100);
            } else {
              // Inter-State or Export
              const igstPercent = parseFloat(item.igst) || 0;
              const cessPercent = parseFloat(item.cess) || 0;
              igst = taxableAmount * (igstPercent / 100);
              cess = taxableAmount * (cessPercent / 100);
            }
            
            const taxAmount = cgst + sgst + igst + cess + customsDuty + tax;
            
            // Get unit label from unit ID/value
            const unitLabel = getUnitLabel(item.unit);
            
            return {
              sku: item.hsn_sac || 'N/A',
              description: item.description || '',
              hsn: item.hsn_sac || '',
              quantity: `${quantity} ${unitLabel}`,
              unitPrice: `${symbol} ${unitPrice.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
              discount: `${symbol} ${discountAmount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
              taxableAmount: `${symbol} ${taxableAmount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
              cgst: `${symbol} ${cgst.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
              sgst: `${symbol} ${sgst.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
              igst: `${symbol} ${igst.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
              cess: `${symbol} ${cess.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
              customsDuty: `${symbol} ${customsDuty.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
              tax: `${symbol} ${tax.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
              taxTotal: `${symbol} ${taxAmount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
              total: `${symbol} ${totalAmount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
            };
          }),
          paymentTerms: {
            paymentTerms: getPaymentTermsLabel(invoice.payment_terms || ''),
            deliveryTerms: getDeliveryTermsLabel(invoice.trade_terms || ''),
            placeOfSupply: invoice.place_of_supply || '',
            shippingMethod: getShippingMethodLabel(invoice.shipping_method || '')
          },
          amountSummary: (() => {
            // Calculate totals from line items
            let totalIGST = 0;
            let totalCESS = 0;
            let totalCGST = 0;
            let totalSGST = 0;
            let totalCustomsDuty = 0;
            let totalTax = 0;
            let calculatedSubtotal = 0;
            let calculatedDiscount = 0;
            
            (invoice.line_items || []).forEach(item => {
              // Calculate subtotal (Qty × Unit Price)
              const qty = parseFloat(item.quantity) || 0;
              const unitPrice = parseFloat(item.unit_price) || 0;
              const itemSubtotal = qty * unitPrice;
              calculatedSubtotal += itemSubtotal;
              
              // Calculate discount amount
              const discount = parseFloat(item.discount) || 0;
              const discountAmount = itemSubtotal * (discount / 100);
              calculatedDiscount += discountAmount;
              
              // Calculate taxable amount (subtotal - discount)
              const taxableAmount = itemSubtotal - discountAmount;
              
              // Tax values are stored as percentages in the database
              // Calculate tax amounts from percentages
              if (isIntraState) {
                const cgstPercent = parseFloat(item.cgst) || 0;
                const sgstPercent = parseFloat(item.sgst) || 0;
                const cessPercent = parseFloat(item.cess) || 0;
                totalCGST += taxableAmount * (cgstPercent / 100);
                totalSGST += taxableAmount * (sgstPercent / 100);
                totalCESS += taxableAmount * (cessPercent / 100);
              } else if (isImport) {
                const customsDutyPercent = parseFloat(item.customs_duty) || 0;
                const igstPercent = parseFloat(item.igst) || 0;
                const cessPercent = parseFloat(item.cess) || 0;
                totalCustomsDuty += taxableAmount * (customsDutyPercent / 100);
                const amountAfterCustoms = taxableAmount + (taxableAmount * (customsDutyPercent / 100));
                totalIGST += amountAfterCustoms * (igstPercent / 100);
                totalCESS += amountAfterCustoms * (cessPercent / 100);
              } else if (isThirdCountry) {
                const taxPercent = parseFloat(item.tax) || 0;
                totalTax += taxableAmount * (taxPercent / 100);
              } else {
                // Inter-State or Export
                const igstPercent = parseFloat(item.igst) || 0;
                const cessPercent = parseFloat(item.cess) || 0;
                totalIGST += taxableAmount * (igstPercent / 100);
                totalCESS += taxableAmount * (cessPercent / 100);
              }
            });
            
            // Use invoice-level totals if available, otherwise calculate from line items
            const subtotal = invoice.subtotal || calculatedSubtotal;
            const totalDiscount = invoice.total_discount || calculatedDiscount;
            const fobValue = invoice.fob_value || (subtotal - totalDiscount);
            const grandTotal = invoice.grand_total || 0;
            
            // Format discount with minus sign
            const currencyCode = getCurrencyCode(invoice.currency || 'INR');
            const symbol = getCurrencySymbol(currencyCode);
            const discountFormatted = totalDiscount > 0 
              ? `- ${symbol} ${totalDiscount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
              : `${symbol} 0.00`;
            
            return {
              subtotal: formatCurrency(subtotal, invoice.currency || 'INR'),
              subtotalRaw: subtotal,
              totalDiscount: discountFormatted,
              totalDiscountRaw: totalDiscount,
              taxableAmount: formatCurrency(fobValue, invoice.currency || 'INR'),
              taxableAmountRaw: fobValue,
              cgst: formatCurrency(totalCGST, invoice.currency || 'INR'),
              cgstRaw: totalCGST,
              sgst: formatCurrency(totalSGST, invoice.currency || 'INR'),
              sgstRaw: totalSGST,
              igst: formatCurrency(totalIGST, invoice.currency || 'INR'),
              igstRaw: totalIGST,
              cess: formatCurrency(totalCESS, invoice.currency || 'INR'),
              cessRaw: totalCESS,
              customsDuty: formatCurrency(totalCustomsDuty, invoice.currency || 'INR'),
              customsDutyRaw: totalCustomsDuty,
              tax: formatCurrency(totalTax, invoice.currency || 'INR'),
              taxRaw: totalTax,
              grandTotal: formatCurrency(grandTotal, invoice.currency || 'INR'),
              grandTotalRaw: grandTotal
            };
          })(),
          bankDetails: {
            bankName: invoice.bank_name_1 || '',
            accountNumber: invoice.account_number_1 || '',
            ifsc: invoice.swift_ifsc_1 || invoice.iban_1 || ''
          },
          packagingInfo: {
            noOfPackages: invoice.no_of_packages || null,
            packagingType: invoice.packaging_type || '',
            grossWeight: invoice.gross_weight || null,
            netWeight: invoice.net_weight || null,
            dimensions: invoice.dimensions || ''
          },
          additionalNotes: invoice.additional_notes || '',
          // Acceptance information (if invoice is accepted)
          acceptanceInfo: invoice.status === 'Accepted' ? {
            decision: invoice.acceptance_decision || 'ACCEPT',
            acceptedByName: invoice.accepted_by_name || '',
            acceptedByDesignation: invoice.accepted_by_designation || '',
            acceptanceDate: invoice.acceptance_date || '',
            paymentCommitmentDate: invoice.payment_commitment_date || '',
            acceptanceNotes: invoice.acceptance_notes || '',
            acceptanceRemarks: invoice.acceptance_remarks || '',
            acceptedAt: invoice.accepted_at || ''
          } : null
        };
        
        return transformedInvoice;
      }
      return null;
    } catch (error) {
      console.error('Error fetching invoice details:', error);
      toast.error('Failed to fetch invoice details');
      return null;
    }
  };


  const handleView = async (invoiceId) => {
    const invoiceDetails = await getInvoiceDetails(invoiceId);
    if (invoiceDetails) {
      setSelectedInvoice(invoiceDetails);
      setIsModalOpen(true);
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedInvoice(null);
  };

  const handleDownloadPDF = () => {
    console.log('Download PDF for invoice:', selectedInvoice?.invoiceNumber);
    // Handle PDF download logic
  };

  const handlePrint = () => {
    console.log('Print invoice:', selectedInvoice?.invoiceNumber);
    window.print();
  };

  const handleShareModal = () => {
    console.log('Share invoice:', selectedInvoice?.invoiceNumber);
    // Handle share logic
  };

  const handleAcceptModal = () => {
    // Open acceptance decision modal
    setIsAcceptanceModalOpen(true);
    // Reset form
    setAcceptanceForm({
      decision: '',
      acceptedBy: '',
      designation: '',
      acceptanceDate: new Date().toISOString().split('T')[0],
      paymentCommitmentDate: '',
      acceptanceNotes: '',
      disputeCategory: '',
      disputeReason: '',
      remarks: ''
    });
  };

  const handleCloseAcceptanceModal = () => {
    setIsAcceptanceModalOpen(false);
    setAcceptanceForm({
      decision: '',
      acceptedBy: '',
      designation: '',
      acceptanceDate: new Date().toISOString().split('T')[0],
      remarks: ''
    });
  };

  const handleAcceptanceFormChange = (field, value) => {
    setAcceptanceForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmitDecision = async () => {
    // Validate required fields
    if (!acceptanceForm.decision) {
      toast.error('Please select an acceptance decision');
      return;
    }
    if (acceptanceForm.decision === 'ACCEPT') {
      if (!acceptanceForm.acceptedBy || !acceptanceForm.designation) {
        toast.error('Please fill in all required fields');
        return;
      }
    }
    if (acceptanceForm.decision === 'DISPUTE') {
      if (!acceptanceForm.acceptedBy || !acceptanceForm.designation || !acceptanceForm.disputeCategory || !acceptanceForm.disputeReason) {
        toast.error('Please fill in all required fields');
        return;
      }
    }

    try {
      setLoading(true);
      
      // Call API to submit acceptance decision
      const response = await CommonService.put(`/seller-invoice/${selectedInvoice.id}/acceptance`, {
        decision: acceptanceForm.decision,
        accepted_by_name: acceptanceForm.acceptedBy,
        accepted_by_designation: acceptanceForm.designation,
        acceptance_date: acceptanceForm.acceptanceDate || null,
        payment_commitment_date: acceptanceForm.paymentCommitmentDate || null,
        acceptance_notes: acceptanceForm.acceptanceNotes || null,
        dispute_category: acceptanceForm.disputeCategory || null,
        dispute_reason: acceptanceForm.disputeReason || null,
        acceptance_remarks: acceptanceForm.remarks || null
      });

      if (response && response.success) {
        toast.success(response.message || `Invoice ${selectedInvoice.invoiceNumber} ${acceptanceForm.decision === 'ACCEPT' ? 'accepted' : 'disputed'} successfully`);
      
        // Close modals
        handleCloseAcceptanceModal();
        handleCloseModal();
        
        // Refresh invoice list
        const refreshResponse = await CommonService.get('/seller-invoice/buyer/my-invoices');
        if (refreshResponse && refreshResponse.success && refreshResponse.data) {
          const transformedInvoices = refreshResponse.data.map(invoice => {
          const displayStatus = resolveInvoiceDisplayStatus(invoice);

          let type = 'Inter-State';
          if (invoice.trade_type) {
            const tradeTypeLower = invoice.trade_type.toLowerCase();
            if (tradeTypeLower.includes('intra-state') || tradeTypeLower.includes('same state')) {
              type = 'Intra-State';
            } else if (tradeTypeLower.includes('export') || tradeTypeLower.includes('abroad')) {
              type = 'Export';
            } else if (tradeTypeLower.includes('inter-state')) {
              type = 'Inter-State';
            }
          }

          const currency = invoice.currency || 'INR';
          
          // Calculate amount: use grand_total if available, otherwise calculate from line items
          let amount = invoice.grand_total;
          if (!amount || amount === 0) {
            // Calculate from line items if grand_total is not available
            if (invoice.line_items && invoice.line_items.length > 0) {
              amount = invoice.line_items.reduce((sum, item) => {
                return sum + (parseFloat(item.total_amount) || 0);
              }, 0);
            } else {
              // Fallback to subtotal + taxes if line items not available
              const subtotal = parseFloat(invoice.subtotal) || 0;
              const fobValue = parseFloat(invoice.fob_value) || subtotal;
              
              // Calculate taxes from line items if available
              let totalTax = 0;
              if (invoice.line_items && invoice.line_items.length > 0) {
                invoice.line_items.forEach(item => {
                  totalTax += (parseFloat(item.cgst) || 0) + 
                             (parseFloat(item.sgst) || 0) + 
                             (parseFloat(item.igst) || 0) + 
                             (parseFloat(item.cess) || 0) + 
                             (parseFloat(item.customs_duty) || 0) + 
                             (parseFloat(item.tax) || 0);
                });
              }
              
              amount = fobValue + totalTax;
            }
          }
          
          const formattedAmount = formatCurrency(amount || 0, currency);

          // Format payment terms
          // Priority: payment_terms label > payment_due_within
          let paymentTermsText = '';
          if (invoice.payment_terms) {
            // First try to get the label from payment_terms ID
            const paymentTermsLabel = getPaymentTermsLabel(invoice.payment_terms);
            if (paymentTermsLabel && paymentTermsLabel.trim() !== '') {
              paymentTermsText = paymentTermsLabel;
            } else if (invoice.payment_due_within) {
              // Fallback to payment_due_within if payment_terms label is not available
              paymentTermsText = `${invoice.payment_due_within} days from invoice date`;
            }
          } else if (invoice.payment_due_within) {
            // Use payment_due_within only if payment_terms is not available
            paymentTermsText = `${invoice.payment_due_within} days from invoice date`;
          }

          return {
            id: invoice.id,
            invoiceNumber: invoice.invoice_number || '',
            status: displayStatus,
            type: type,
            poNumber: invoice.po_reference || invoice.po_number || '',
            seller: invoice.seller_company_name || '',
            invoiceDate: invoice.invoice_date || '',
            paymentTerms: paymentTermsText,
            dueDate: invoice.due_date || '',
            amount: formattedAmount,
            currency: currency,
            buyerApprovalStatus: invoice.buyer_approval_status || 'not_required',
            buyerApprovalPending: invoice.buyer_approval_pending === true,
            canAccept: invoice.can_accept !== false,
            fullInvoiceData: invoice
          };
          });
          
          setInvoices(transformedInvoices);
        } else {
          // If refresh fails, still show success but log warning
          console.warn('Failed to refresh invoice list after acceptance');
        }
      } else {
        toast.error(response?.error || 'Failed to submit acceptance decision');
      }
    } catch (error) {
      console.error('Error submitting acceptance decision:', error);
      if (error.response?.status === 401) {
        toast.error('Unauthorized. Please login again.');
        setTimeout(() => {
          window.location.href = '/login';
        }, 2000);
      } else {
        toast.error(error.response?.data?.error || error.message || 'Failed to submit acceptance decision');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleShare = (invoiceId) => {
    console.log('Share invoice:', invoiceId);
    // Handle share logic here
  };

  const handleAccept = async (invoiceId) => {
    // Find the invoice from the list
    const invoice = invoices.find(inv => inv.id === invoiceId);
    if (!invoice) {
      toast.error('Invoice not found');
      return;
    }

    // Get full invoice details
    const invoiceDetails = await getInvoiceDetails(invoiceId);
    if (invoiceDetails) {
      setSelectedInvoice(invoiceDetails);
      setIsAcceptanceModalOpen(true);
      // Reset form
      setAcceptanceForm({
        decision: '',
        acceptedBy: '',
        designation: '',
        acceptanceDate: new Date().toISOString().split('T')[0],
        paymentCommitmentDate: '',
        acceptanceNotes: '',
        remarks: ''
      });
    } else {
      toast.error('Failed to load invoice details');
    }
  };

  const isOverdue = (dueDate) => {
    if (!dueDate) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const due = new Date(dueDate);
    due.setHours(0, 0, 0, 0);
    return due < today;
  };

  return (
    <div className="approve-invoice-list">
      <div className="ail-main-content">
        {/* Header Section */}
        <div className="ail-header">
          <div className="ail-header-left">
            <h1 className="ail-main-title">Invoice Acceptance & Payment</h1>
            <p className="ail-subtitle">Review and accept invoices from suppliers</p>
          </div>
          <div className="ail-header-right">
            <button className="ail-back-btn" onClick={() => navigate(-1)}>
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="m12 19-7-7 7-7"></path>
                <path d="M19 12H5"></path>
              </svg>
              Back
            </button>
          </div>
        </div>

        {/* Search and Filter Section */}
        <div className="ail-search-filter-section">
          <div className="ail-search-filter-row">
            <div className="ail-search-wrapper">
              <h3 className="ail-section-heading">Search Invoices</h3>
              <div className="ail-search-input-wrapper">
                <svg 
                  className="ail-search-icon" 
                  xmlns="http://www.w3.org/2000/svg" 
                  width="20" 
                  height="20" 
                  viewBox="0 0 24 24" 
                  fill="none" 
                  stroke="currentColor" 
                  strokeWidth="2" 
                  strokeLinecap="round" 
                  strokeLinejoin="round"
                >
                  <circle cx="11" cy="11" r="8"></circle>
                  <path d="m21 21-4.35-4.35"></path>
                </svg>
                <input
                  type="text"
                  className="ail-search-input"
                  placeholder="Search by Invoice Number, PO Number, or Supplier Name..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
            </div>
            <div className="ail-filter-wrapper">
              <h3 className="ail-section-heading">Filter by Status</h3>
              <select
                className="ail-filter-select"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                {statusOptions.map(option => (
                  <option key={option} value={option}>{option}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Invoices List Section */}
        <div className="ail-invoices-section">
          <h2 className="ail-invoices-title">Invoices ({filteredInvoices.length})</h2>
          
          {loading ? (
            <div className="ail-empty-state">
              <p>Loading invoices...</p>
            </div>
          ) : filteredInvoices.length === 0 ? (
            <div className="ail-empty-state">
              <p>No invoices found matching your search criteria.</p>
            </div>
          ) : (
            <div className="ail-invoice-cards">
              {filteredInvoices.map(invoice => (
                <div key={invoice.id} className="ail-invoice-card">
                  <div className="ail-card-header">
                    <div className="ail-invoice-header-left">
                      <h3 className="ail-invoice-number">{invoice.invoiceNumber}</h3>
                      <div className="ail-badges">
                        <span className={`ail-status-badge ${getStatusClass(invoice.status)}`}>
                          {invoice.status}
                        </span>
                        <span className={`ail-type-badge ${getTypeClass(invoice.type)}`}>
                          {invoice.type}
                        </span>
                      </div>
                    </div>
                    <div className="ail-card-actions">
                      {invoice.status === 'Accepted' ? (
                        <button 
                          className="ail-action-btn ail-btn-view-accepted"
                          onClick={() => handleView(invoice.id)}
                        >
                          <svg 
                            xmlns="http://www.w3.org/2000/svg" 
                            width="16" 
                            height="16" 
                            viewBox="0 0 24 24" 
                            fill="none" 
                            stroke="currentColor" 
                            strokeWidth="2" 
                            strokeLinecap="round" 
                            strokeLinejoin="round"
                          >
                            <path d="M2.062 12.348a1 1 0 0 1 0-.696 10.75 10.75 0 0 1 19.876 0 1 1 0 0 1 0 .696 10.75 10.75 0 0 1-19.876 0"></path>
                            <circle cx="12" cy="12" r="3"></circle>
                          </svg>
                          View Accepted Invoice Details
                        </button>
                      ) : (
                        <>
                          <button 
                            className="ail-action-btn ail-btn-view"
                            onClick={() => handleView(invoice.id)}
                          >
                            <svg 
                              xmlns="http://www.w3.org/2000/svg" 
                              width="16" 
                              height="16" 
                              viewBox="0 0 24 24" 
                              fill="none" 
                              stroke="currentColor" 
                              strokeWidth="2" 
                              strokeLinecap="round" 
                              strokeLinejoin="round"
                            >
                              <path d="M2.062 12.348a1 1 0 0 1 0-.696 10.75 10.75 0 0 1 19.876 0 1 1 0 0 1 0 .696 10.75 10.75 0 0 1-19.876 0"></path>
                              <circle cx="12" cy="12" r="3"></circle>
                            </svg>
                            View
                          </button>
                          <button 
                            className="ail-action-btn ail-btn-share"
                            onClick={() => handleShare(invoice.id)}
                          >
                            <svg 
                              xmlns="http://www.w3.org/2000/svg" 
                              width="16" 
                              height="16" 
                              viewBox="0 0 24 24" 
                              fill="none" 
                              stroke="currentColor" 
                              strokeWidth="2" 
                              strokeLinecap="round" 
                              strokeLinejoin="round"
                            >
                              <circle cx="18" cy="5" r="3"></circle>
                              <circle cx="6" cy="12" r="3"></circle>
                              <circle cx="18" cy="19" r="3"></circle>
                              <line x1="8.59" x2="15.42" y1="13.51" y2="17.49"></line>
                              <line x1="15.41" x2="8.59" y1="6.51" y2="10.49"></line>
                            </svg>
                            
                          </button>
                          {canShowAcceptInvoice(invoice) && (
                            <button 
                              className="ail-action-btn ail-btn-accept"
                              onClick={() => handleAccept(invoice.id)}
                            >
                              <svg 
                                xmlns="http://www.w3.org/2000/svg" 
                                width="16" 
                                height="16" 
                                viewBox="0 0 24 24" 
                                fill="none" 
                                stroke="currentColor" 
                                strokeWidth="2" 
                                strokeLinecap="round" 
                                strokeLinejoin="round"
                              >
                                <polyline points="20 6 9 17 4 12"></polyline>
                              </svg>
                              Accept
                            </button>
                          )}
                        </>
                      )}
                    </div>
                  </div>

                  <div className="ail-card-body">
                    <div className="ail-details-grid">
                      <div className="ail-grid-item">
                        <p className="ail-detail-label">PO Number</p>
                        <p className="ail-detail-value">{invoice.poNumber}</p>
                      </div>
                      <div className="ail-grid-item">
                        <p className="ail-detail-label">Seller</p>
                        <p className="ail-detail-value">{invoice.seller}</p>
                      </div>
                      <div className="ail-grid-item">
                        <p className="ail-detail-label">Invoice Date</p>
                        <p className="ail-detail-value">{formatDate(invoice.invoiceDate)}</p>
                      </div>
                      <div className="ail-grid-item">
                        <p className="ail-detail-label">Payment Terms</p>
                        <p className="ail-detail-value">{invoice.paymentTerms}</p>
                      </div>
                      <div className="ail-grid-item">
                        <p className="ail-detail-label">Due Date</p>
                        <p className={`ail-detail-value ${isOverdue(invoice.dueDate) ? 'ail-due-date-overdue' : ''}`}>
                          {formatDate(invoice.dueDate)}
                        </p>
                      </div>
                      <div className="ail-grid-item">
                        <p className="ail-detail-label">Amount</p>
                        <p className="ail-detail-value ail-amount">{invoice.amount}</p>
                      </div>
                    </div>

                    {invoice.status === 'Overdue' && (
                      <div className="ail-overdue-alert">
                        <svg 
                          xmlns="http://www.w3.org/2000/svg" 
                          width="16" 
                          height="16" 
                          viewBox="0 0 24 24" 
                          fill="none" 
                          stroke="currentColor" 
                          strokeWidth="2" 
                          strokeLinecap="round" 
                          strokeLinejoin="round"
                          className="ail-alert-icon"
                        >
                          <circle cx="12" cy="12" r="10"></circle>
                          <line x1="12" y1="8" x2="12" y2="12"></line>
                          <line x1="12" y1="16" x2="12.01" y2="16"></line>
                        </svg>
                        <span className="ail-alert-text">
                          Payment overdue! Please clear immediately.
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Invoice Details Modal */}
      {isModalOpen && selectedInvoice && (
        <div className="ail-modal-overlay" onClick={handleCloseModal}>
          <div
            className="ail-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="ail-modal-title"
            aria-describedby="ail-modal-description"
            tabIndex="-1"
            onClick={(e) => e.stopPropagation()}
          >
            <button 
              className="ail-modal-close" 
              onClick={handleCloseModal}
              aria-label="Close invoice details"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M18 6 6 18"></path>
                <path d="m6 6 12 12"></path>
              </svg>
            </button>

            <div className="ail-modal-header">
              <div>
                <h2 id="ail-modal-title" className="ail-modal-title">Invoice Details - {selectedInvoice.invoiceNumber}</h2>
                <p id="ail-modal-description" className="ail-modal-subtitle">Complete invoice information with all items and tax details</p>
              </div>
            </div>

            <div className="ail-modal-content">
              {/* Summary Grid */}
              <div className="ail-summary-grid">
                <div className="ail-summary-item">
                  <label className="ail-summary-label">Invoice Number</label>
                  <p className="ail-summary-value ail-value-mono">{selectedInvoice.invoiceNumber}</p>
                </div>
                <div className="ail-summary-item">
                  <label className="ail-summary-label">Invoice Date</label>
                  <p className="ail-summary-value">{formatDate(selectedInvoice.invoiceDate)}</p>
                </div>
                <div className="ail-summary-item">
                  <label className="ail-summary-label">Due Date</label>
                  <p className="ail-summary-value">{formatDate(selectedInvoice.dueDate)}</p>
                </div>
                <div className="ail-summary-item">
                  <label className="ail-summary-label">PO Number</label>
                  <p className="ail-summary-value ail-value-mono">{selectedInvoice.poNumber}</p>
                </div>
                <div className="ail-summary-item">
                  <label className="ail-summary-label">Currency</label>
                  <p className="ail-summary-value">{selectedInvoice.currency}</p>
                </div>
                <div className="ail-summary-item">
                  <label className="ail-summary-label">Status</label>
                  <div className="ail-summary-status">
                    <span className={`ail-status-badge ${getStatusClass(selectedInvoice.status)}`}>
                      {selectedInvoice.status}
                    </span>
                  </div>
                </div>
              </div>

              {selectedInvoice.buyerApprovalPending && selectedInvoice.buyerApproval ? (
                <div className="ail-approval-panel">
                  <h3 className="ail-section-heading">Buyer invoice approval</h3>
                  <p className="ail-approval-hint">
                    This invoice is routed through your organisation&apos;s invoice approval matrix.
                    You can accept it after all required approvals are completed.
                  </p>
                  <div className="ail-approval-meta">
                    <span className="ail-approval-badge">Pending Buyer Approval</span>
                    <span>
                      Level {selectedInvoice.buyerApproval.current_level} of{' '}
                      {selectedInvoice.buyerApproval.approval_level_count}
                    </span>
                    {selectedInvoice.buyerApproval.policy_name ? (
                      <span>Policy: {selectedInvoice.buyerApproval.policy_name}</span>
                    ) : null}
                    {selectedInvoice.buyerApproval.current_assignee_name ? (
                      <span>
                        Current approver: {selectedInvoice.buyerApproval.current_assignee_name}
                      </span>
                    ) : null}
                  </div>
                  {selectedInvoice.buyerApproval.actions?.length > 0 ? (
                    <div className="ail-approval-history">
                      <h4 className="ail-approval-history-title">Approval history</h4>
                      <ul className="ail-approval-history-list">
                        {selectedInvoice.buyerApproval.actions.map((action) => (
                          <li key={action.id} className="ail-approval-history-item">
                            <div className="ail-approval-history-head">
                              <strong>{String(action.action_type || '').replace(/_/g, ' ')}</strong>
                              {action.action_by_name ? (
                                <span> — {action.action_by_name}</span>
                              ) : null}
                            </div>
                            {action.comments ? (
                              <p className="ail-approval-history-comment">{action.comments}</p>
                            ) : null}
                          </li>
                        ))}
                      </ul>
                    </div>
                  ) : null}
                </div>
              ) : null}

              {selectedInvoice.buyerApprovalStatus === 'approved' &&
              selectedInvoice.status === 'Approved' ? (
                <div className="ail-approval-panel ail-approval-panel-approved">
                  <h3 className="ail-section-heading">Buyer approval completed</h3>
                  <p className="ail-approval-hint">
                    All required buyer approvals are complete. You may now accept this invoice.
                  </p>
                  {selectedInvoice.buyerApproval?.actions?.length > 0 ? (
                    <div className="ail-approval-history">
                      <h4 className="ail-approval-history-title">Approval history</h4>
                      <ul className="ail-approval-history-list">
                        {selectedInvoice.buyerApproval.actions.map((action) => (
                          <li key={action.id} className="ail-approval-history-item">
                            <div className="ail-approval-history-head">
                              <strong>{String(action.action_type || '').replace(/_/g, ' ')}</strong>
                              {action.action_by_name ? (
                                <span> — {action.action_by_name}</span>
                              ) : null}
                            </div>
                            {action.comments ? (
                              <p className="ail-approval-history-comment">{action.comments}</p>
                            ) : null}
                          </li>
                        ))}
                      </ul>
                    </div>
                  ) : null}
                </div>
              ) : null}

              {selectedInvoice.buyerApprovalStatus === 'rejected' ? (
                <div className="ail-approval-panel ail-approval-panel-rejected">
                  <h3 className="ail-section-heading">Invoice approval rejected</h3>
                  <p className="ail-approval-hint">
                    Internal approval for this invoice was rejected. Contact your approvers or the supplier.
                  </p>
                </div>
              ) : null}

              {/* Seller Details */}
              <div className="ail-section">
                <h3 className="ail-section-title ail-section-title-orange">
                  Seller / Supplier Details
                </h3>
                <div className="ail-section-card ail-card-orange">
                  <div className="ail-section-grid ail-grid-4">
                    <div>
                      <label className="ail-section-label">Company Name</label>
                      <p className="ail-section-value">{selectedInvoice.seller.companyName}</p>
                    </div>
                    <div>
                      <label className="ail-section-label">GSTIN</label>
                      <p className="ail-section-value ail-value-mono">{selectedInvoice.seller.gstin}</p>
                    </div>
                    <div>
                      <label className="ail-section-label">Email</label>
                      <p className="ail-section-value">{selectedInvoice.seller.email || '—'}</p>
                    </div>
                    <div>
                      <label className="ail-section-label">Phone</label>
                      <p className="ail-section-value">{selectedInvoice.seller.phone || '—'}</p>
                    </div>
                    <div style={{ gridColumn: 'span 4' }}>
                      <label className="ail-section-label">Address</label>
                      <p className="ail-section-value">{selectedInvoice.seller.address}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Buyer Details */}
              <div className="ail-section">
                <h3 className="ail-section-title ail-section-title-green">
                  Buyer Details
                </h3>
                <div className="ail-section-card ail-card-green">
                  <div className="ail-section-grid ail-grid-4">
                    <div>
                      <label className="ail-section-label">Company Name</label>
                      <p className="ail-section-value">{selectedInvoice.buyer.companyName}</p>
                    </div>
                    <div>
                      <label className="ail-section-label">GSTIN</label>
                      <p className="ail-section-value ail-value-mono">{selectedInvoice.buyer.gstin}</p>
                    </div>
                    <div style={{ gridColumn: 'span 4' }}>
                      <label className="ail-section-label">Address</label>
                      <p className="ail-section-value">{selectedInvoice.buyer.address}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Invoice Items */}
              <div className="ail-section">
                <h3 className="ail-section-title ail-section-title-amber">
                  Invoice Items
                </h3>
                <div className="ail-table-wrapper">
                  <table className="ail-table">
                    <thead>
                      <tr>
                        <th>SKU</th>
                        <th>Description</th>
                        <th>HSN</th>
                        <th className="ail-text-right">Qty</th>
                        <th className="ail-text-right">Unit Price</th>
                        <th className="ail-text-right">Discount</th>
                        <th className="ail-text-right">Taxable Amt</th>
                        {/* Conditional tax columns based on trade type */}
                        {selectedInvoice.isIntraState ? (
                          <>
                            <th className="ail-text-right">CGST</th>
                            <th className="ail-text-right">SGST</th>
                            <th className="ail-text-right">CESS</th>
                          </>
                        ) : selectedInvoice.isImport ? (
                          <>
                            <th className="ail-text-right">Customs Duty</th>
                            <th className="ail-text-right">IGST</th>
                            <th className="ail-text-right">CESS</th>
                          </>
                        ) : selectedInvoice.isThirdCountry ? (
                          <>
                            <th className="ail-text-right">Tax</th>
                          </>
                        ) : (
                          <>
                            <th className="ail-text-right">IGST</th>
                            <th className="ail-text-right">CESS</th>
                          </>
                        )}
                        <th className="ail-text-right">Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedInvoice.items.map((item, index) => (
                        <tr key={index}>
                          <td className="ail-value-mono ail-text-small">{item.sku}</td>
                          <td>{item.description}</td>
                          <td className="ail-value-mono ail-text-small">{item.hsn}</td>
                          <td className="ail-text-right">{item.quantity}</td>
                          <td className="ail-text-right">{item.unitPrice}</td>
                          <td className="ail-text-right">{item.discount}</td>
                          <td className="ail-text-right">{item.taxableAmount}</td>
                          {/* Conditional tax cells based on trade type */}
                          {selectedInvoice.isIntraState ? (
                            <>
                              <td className="ail-text-right">{item.cgst}</td>
                              <td className="ail-text-right">{item.sgst}</td>
                              <td className="ail-text-right">{item.cess}</td>
                            </>
                          ) : selectedInvoice.isImport ? (
                            <>
                              <td className="ail-text-right">{item.customsDuty}</td>
                              <td className="ail-text-right">{item.igst}</td>
                              <td className="ail-text-right">{item.cess}</td>
                            </>
                          ) : selectedInvoice.isThirdCountry ? (
                            <>
                              <td className="ail-text-right">{item.tax}</td>
                            </>
                          ) : (
                            <>
                              <td className="ail-text-right">{item.igst}</td>
                              <td className="ail-text-right">{item.cess}</td>
                            </>
                          )}
                          <td className="ail-text-right">{item.total}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Amount Summary */}
              <div className="ail-section">
                <h3 className="ail-section-title ail-section-title-indigo">
                  Amount Summary
                </h3>
                <div className="ail-section-card ail-card-indigo">
                  <div className="ail-amount-summary">
                    <div className="ail-amount-row">
                      <span className="ail-amount-label">Subtotal (Qty × Unit Price):</span>
                      <span className="ail-amount-value">{selectedInvoice.amountSummary.subtotal}</span>
                    </div>
                    <div className="ail-amount-row">
                      <span className="ail-amount-label">Total Discount:</span>
                      <span className="ail-amount-value">{selectedInvoice.amountSummary.totalDiscount}</span>
                    </div>
                    <div className="ail-amount-row">
                      <span className="ail-amount-label">Taxable Amount:</span>
                      <span className="ail-amount-value">{selectedInvoice.amountSummary.taxableAmount}</span>
                    </div>
                    <div className="ail-amount-divider"></div>
                    {/* Conditional tax fields based on trade type */}
                    {selectedInvoice.isIntraState ? (
                      <>
                        {(selectedInvoice.amountSummary.cgstRaw || 0) > 0 && (
                          <div className="ail-amount-row">
                            <span className="ail-amount-label">CGST:</span>
                            <span className="ail-amount-value">{selectedInvoice.amountSummary.cgst}</span>
                          </div>
                        )}
                        {(selectedInvoice.amountSummary.sgstRaw || 0) > 0 && (
                          <div className="ail-amount-row">
                            <span className="ail-amount-label">SGST:</span>
                            <span className="ail-amount-value">{selectedInvoice.amountSummary.sgst}</span>
                          </div>
                        )}
                        {(selectedInvoice.amountSummary.cessRaw || 0) > 0 && (
                          <div className="ail-amount-row">
                            <span className="ail-amount-label">CESS:</span>
                            <span className="ail-amount-value">{selectedInvoice.amountSummary.cess}</span>
                          </div>
                        )}
                      </>
                    ) : selectedInvoice.isImport ? (
                      <>
                        {(selectedInvoice.amountSummary.customsDutyRaw || 0) > 0 && (
                          <div className="ail-amount-row">
                            <span className="ail-amount-label">Customs Duty:</span>
                            <span className="ail-amount-value">{selectedInvoice.amountSummary.customsDuty}</span>
                          </div>
                        )}
                        {(selectedInvoice.amountSummary.igstRaw || 0) > 0 && (
                          <div className="ail-amount-row">
                            <span className="ail-amount-label">IGST:</span>
                            <span className="ail-amount-value">{selectedInvoice.amountSummary.igst}</span>
                          </div>
                        )}
                        {(selectedInvoice.amountSummary.cessRaw || 0) > 0 && (
                          <div className="ail-amount-row">
                            <span className="ail-amount-label">CESS:</span>
                            <span className="ail-amount-value">{selectedInvoice.amountSummary.cess}</span>
                          </div>
                        )}
                      </>
                    ) : selectedInvoice.isThirdCountry ? (
                      <>
                        {(selectedInvoice.amountSummary.taxRaw || 0) > 0 && (
                          <div className="ail-amount-row">
                            <span className="ail-amount-label">Tax:</span>
                            <span className="ail-amount-value">{selectedInvoice.amountSummary.tax}</span>
                          </div>
                        )}
                      </>
                    ) : (
                      <>
                        {/* For Inter-State/Export: Always show IGST and CESS if they exist, even if 0 */}
                        <div className="ail-amount-row">
                          <span className="ail-amount-label">IGST:</span>
                          <span className="ail-amount-value">
                            {selectedInvoice.amountSummary.igst || formatCurrency(0, selectedInvoice.currencyValue || 'INR')}
                          </span>
                        </div>
                        <div className="ail-amount-row">
                          <span className="ail-amount-label">CESS:</span>
                          <span className="ail-amount-value">
                            {selectedInvoice.amountSummary.cess || formatCurrency(0, selectedInvoice.currencyValue || 'INR')}
                          </span>
                        </div>
                      </>
                    )}
                    <div className="ail-amount-divider"></div>
                    <div className="ail-amount-row ail-amount-total">
                      <span className="ail-amount-label">Grand Total (Sum of Line Items):</span>
                      <span className="ail-amount-value">{selectedInvoice.amountSummary.grandTotal}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Payment & Delivery Terms */}
              <div className="ail-section">
                <h3 className="ail-section-title ail-section-title-purple">
                  Payment & Delivery Terms
                </h3>
                <div className="ail-section-card ail-card-purple">
                  <div className="ail-section-grid ail-grid-4">
                    <div>
                      <label className="ail-section-label">Payment Terms</label>
                      <p className="ail-section-value">{selectedInvoice.paymentTerms.paymentTerms}</p>
                    </div>
                    <div>
                      <label className="ail-section-label">Delivery Terms</label>
                      <p className="ail-section-value">{selectedInvoice.paymentTerms.deliveryTerms}</p>
                    </div>
                    <div>
                      <label className="ail-section-label">Place of Supply</label>
                      <p className="ail-section-value">{selectedInvoice.paymentTerms.placeOfSupply}</p>
                    </div>
                    <div>
                      <label className="ail-section-label">Shipping Method</label>
                      <p className="ail-section-value">{selectedInvoice.paymentTerms.shippingMethod}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Packaging Information */}
              {(selectedInvoice.packagingInfo?.noOfPackages || 
                selectedInvoice.packagingInfo?.packagingType || 
                selectedInvoice.packagingInfo?.grossWeight || 
                selectedInvoice.packagingInfo?.netWeight || 
                selectedInvoice.packagingInfo?.dimensions) && (
                <div className="ail-section">
                  <h3 className="ail-section-title ail-section-title-teal">
                    Packaging Information
                  </h3>
                  <div className="ail-section-card ail-card-blue">
                    <div className="ail-section-grid ail-grid-5">
                      {selectedInvoice.packagingInfo.noOfPackages && (
                        <div>
                          <label className="ail-section-label">No. of Packages</label>
                          <p className="ail-section-value">{selectedInvoice.packagingInfo.noOfPackages}</p>
                        </div>
                      )}
                      {selectedInvoice.packagingInfo.packagingType && (
                        <div>
                          <label className="ail-section-label">Packaging Type</label>
                          <p className="ail-section-value">{selectedInvoice.packagingInfo.packagingType}</p>
                        </div>
                      )}
                      {selectedInvoice.packagingInfo.grossWeight && (
                        <div>
                          <label className="ail-section-label">Gross Weight</label>
                          <p className="ail-section-value">{selectedInvoice.packagingInfo.grossWeight} kg</p>
                        </div>
                      )}
                      {selectedInvoice.packagingInfo.netWeight && (
                        <div>
                          <label className="ail-section-label">Net Weight</label>
                          <p className="ail-section-value">{selectedInvoice.packagingInfo.netWeight} kg</p>
                        </div>
                      )}
                      {selectedInvoice.packagingInfo.dimensions && (
                        <div>
                          <label className="ail-section-label">Dimensions</label>
                          <p className="ail-section-value">{selectedInvoice.packagingInfo.dimensions}</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Bank Details */}
              <div className="ail-section">
                <h3 className="ail-section-title ail-section-title-blue">
                  Bank Details for Payment
                </h3>
                <div className="ail-section-card ail-card-blue">
                  <div className="ail-section-grid ail-grid-3">
                    <div>
                      <label className="ail-section-label">Bank Name</label>
                      <p className="ail-section-value">{selectedInvoice.bankDetails.bankName}</p>
                    </div>
                    <div>
                      <label className="ail-section-label">Account Number</label>
                      <p className="ail-section-value ail-value-mono">{selectedInvoice.bankDetails.accountNumber}</p>
                    </div>
                    <div>
                      <label className="ail-section-label">IFSC / SWIFT Code</label>
                      <p className="ail-section-value ail-value-mono">{selectedInvoice.bankDetails.ifsc}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Additional Notes */}
              {selectedInvoice.additionalNotes && (
                <div className="ail-section">
                  <h3 className="ail-section-title ail-section-title-gray">
                    Additional Notes
                  </h3>
                  <div className="ail-section-card ail-card-gray">
                    <p className="ail-section-value">{selectedInvoice.additionalNotes}</p>
                  </div>
                </div>
              )}

              {/* Acceptance Information - Show only for accepted invoices */}
              {selectedInvoice.status === 'Accepted' && selectedInvoice.acceptanceInfo && (
                <div className="ail-section">
                  <h3 className="ail-section-title ail-section-title-green">
                    Acceptance Information
                  </h3>
                  <div className="ail-section-card ail-card-green">
                    <div className="ail-section-grid ail-grid-4">
                      <div>
                        <label className="ail-section-label">Accepted By</label>
                        <p className="ail-section-value">{selectedInvoice.acceptanceInfo.acceptedByName || '—'}</p>
                      </div>
                      <div>
                        <label className="ail-section-label">Designation</label>
                        <p className="ail-section-value">{selectedInvoice.acceptanceInfo.acceptedByDesignation || '—'}</p>
                      </div>
                      <div>
                        <label className="ail-section-label">Acceptance Date</label>
                        <p className="ail-section-value">{selectedInvoice.acceptanceInfo.acceptanceDate ? formatDate(selectedInvoice.acceptanceInfo.acceptanceDate) : '—'}</p>
                      </div>
                      <div>
                        <label className="ail-section-label">Payment Commitment Date</label>
                        <p className="ail-section-value">{selectedInvoice.acceptanceInfo.paymentCommitmentDate ? formatDate(selectedInvoice.acceptanceInfo.paymentCommitmentDate) : '—'}</p>
                      </div>
                      {selectedInvoice.acceptanceInfo.acceptanceNotes && (
                        <div style={{ gridColumn: 'span 4' }}>
                          <label className="ail-section-label">Acceptance Notes</label>
                          <p className="ail-section-value">{selectedInvoice.acceptanceInfo.acceptanceNotes}</p>
                        </div>
                      )}
                      {selectedInvoice.acceptanceInfo.acceptanceRemarks && (
                        <div style={{ gridColumn: 'span 4' }}>
                          <label className="ail-section-label">Additional Remarks</label>
                          <p className="ail-section-value">{selectedInvoice.acceptanceInfo.acceptanceRemarks}</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="ail-modal-footer">
              <button 
                className="ail-btn ail-btn-secondary"
                onClick={handleDownloadPDF}
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 15V3"></path>
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                  <path d="m7 10 5 5 5-5"></path>
                </svg>
                Download PDF
              </button>
              <button 
                className="ail-btn ail-btn-secondary"
                onClick={handlePrint}
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"></path>
                  <path d="M6 9V3a1 1 0 0 1 1-1h10a1 1 0 0 1 1 1v6"></path>
                  <rect x="6" y="14" width="12" height="8" rx="1"></rect>
                </svg>
                Print
              </button>
              <button 
                className="ail-btn ail-btn-secondary"
                onClick={handleShareModal}
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="18" cy="5" r="3"></circle>
                  <circle cx="6" cy="12" r="3"></circle>
                  <circle cx="18" cy="19" r="3"></circle>
                  <line x1="8.59" x2="15.42" y1="13.51" y2="17.49"></line>
                  <line x1="15.41" x2="8.59" y1="6.51" y2="10.49"></line>
                </svg>
                Share
              </button>
              {canShowAcceptInvoice(selectedInvoice) && (
                <button 
                  className="ail-btn ail-btn-primary"
                  onClick={handleAcceptModal}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21.801 10A10 10 0 1 1 17 3.335"></path>
                    <path d="m9 11 3 3L22 4"></path>
                  </svg>
                  Accept Invoice
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Invoice Acceptance Decision Modal */}
      {isAcceptanceModalOpen && selectedInvoice && (
        <div className="ail-modal-overlay" onClick={handleCloseAcceptanceModal}>
          <div
            className="ail-modal ail-acceptance-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="ail-acceptance-modal-title"
            onClick={(e) => e.stopPropagation()}
          >
            <button 
              className="ail-modal-close" 
              onClick={handleCloseAcceptanceModal}
              aria-label="Close acceptance modal"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M18 6 6 18"></path>
                <path d="m6 6 12 12"></path>
              </svg>
            </button>

            <div className="ail-modal-header">
              <div>
                <h2 id="ail-acceptance-modal-title" className="ail-modal-title">
                  Invoice Acceptance Decision - {selectedInvoice.invoiceNumber}
                </h2>
                <p className="ail-modal-subtitle">
                  Review and provide your acceptance decision for this invoice
                </p>
              </div>
            </div>

            <div className="ail-modal-content">
              {/* Invoice Summary Box */}
              <div className="ail-acceptance-summary-box">
                <div className="ail-acceptance-summary-grid">
                  <div className="ail-acceptance-summary-item">
                    <label className="ail-acceptance-summary-label">Invoice Amount</label>
                    <p className="ail-acceptance-summary-value ail-amount">{selectedInvoice.amount}</p>
                  </div>
                  <div className="ail-acceptance-summary-item">
                    <label className="ail-acceptance-summary-label">Due Date</label>
                    <p className="ail-acceptance-summary-value">{formatDate(selectedInvoice.dueDate)}</p>
                  </div>
                  <div className="ail-acceptance-summary-item">
                    <label className="ail-acceptance-summary-label">Payment Terms</label>
                    <p className="ail-acceptance-summary-value">
                      {typeof selectedInvoice.paymentTerms === 'object' 
                        ? selectedInvoice.paymentTerms.paymentTerms 
                        : selectedInvoice.paymentTerms}
                    </p>
                  </div>
                  <div className="ail-acceptance-summary-item">
                    <label className="ail-acceptance-summary-label">Supplier</label>
                    <p className="ail-acceptance-summary-value">
                      {typeof selectedInvoice.seller === 'object' 
                        ? selectedInvoice.seller.companyName 
                        : selectedInvoice.seller}
                    </p>
                  </div>
                </div>
              </div>

              {/* Acceptance Decision Radio Buttons */}
              <div className="ail-acceptance-decision-section">
                <label className="ail-acceptance-decision-label">
                  Acceptance Decision <span className="ail-required">*</span>
                </label>
                <div className="ail-acceptance-radio-group">
                  <div 
                    className={`ail-acceptance-radio-option ${acceptanceForm.decision === 'ACCEPT' ? 'ail-radio-selected' : ''}`}
                    onClick={() => handleAcceptanceFormChange('decision', 'ACCEPT')}
                  >
                    <button
                      type="button"
                      role="radio"
                      aria-checked={acceptanceForm.decision === 'ACCEPT'}
                      className="ail-radio-button"
                    >
                      {acceptanceForm.decision === 'ACCEPT' && (
                        <span className="ail-radio-check"></span>
                      )}
                    </button>
                    <label className="ail-radio-label">
                      <div className="ail-radio-content">
                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="ail-radio-icon ail-icon-accept">
                          <path d="M21.801 10A10 10 0 1 1 17 3.335"></path>
                          <path d="m9 11 3 3L22 4"></path>
                        </svg>
                        <div>
                          <p className="ail-radio-title">Accept Invoice</p>
                          <p className="ail-radio-description">Accept the invoice and commit to payment</p>
                        </div>
                      </div>
                    </label>
                  </div>
                  <div 
                    className={`ail-acceptance-radio-option ${acceptanceForm.decision === 'DISPUTE' ? 'ail-radio-selected' : ''}`}
                    onClick={() => handleAcceptanceFormChange('decision', 'DISPUTE')}
                  >
                    <button
                      type="button"
                      role="radio"
                      aria-checked={acceptanceForm.decision === 'DISPUTE'}
                      className="ail-radio-button"
                    >
                      {acceptanceForm.decision === 'DISPUTE' && (
                        <span className="ail-radio-check"></span>
                      )}
                    </button>
                    <label className="ail-radio-label">
                      <div className="ail-radio-content">
                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="ail-radio-icon ail-icon-dispute">
                          <circle cx="12" cy="12" r="10"></circle>
                          <path d="m15 9-6 6"></path>
                          <path d="m9 9 6 6"></path>
                        </svg>
                        <div>
                          <p className="ail-radio-title">Raise Dispute</p>
                          <p className="ail-radio-description">Dispute the invoice due to discrepancies</p>
                        </div>
                      </div>
                    </label>
                  </div>
                </div>
              </div>

              {/* Form Fields */}
              {acceptanceForm.decision === 'ACCEPT' && (
                <div className="ail-acceptance-form-fields">
                  {/* Row 1: Accepted By and Designation */}
                  <div className="ail-acceptance-form-row">
                    <div className="ail-acceptance-form-field">
                      <label className="ail-acceptance-field-label">
                        Accepted By (Name) <span className="ail-required">*</span>
                      </label>
                      <input
                        type="text"
                        className="ail-acceptance-input"
                        placeholder="Your full name"
                        value={acceptanceForm.acceptedBy}
                        onChange={(e) => handleAcceptanceFormChange('acceptedBy', e.target.value)}
                      />
                    </div>
                    <div className="ail-acceptance-form-field">
                      <label className="ail-acceptance-field-label">
                        Designation <span className="ail-required">*</span>
                      </label>
                      <input
                        type="text"
                        className="ail-acceptance-input"
                        placeholder="e.g., Finance Manager"
                        value={acceptanceForm.designation}
                        onChange={(e) => handleAcceptanceFormChange('designation', e.target.value)}
                      />
                    </div>
                  </div>
                  
                  {/* Row 2: Acceptance Date and Payment Commitment Date */}
                  <div className="ail-acceptance-form-row">
                    <div className="ail-acceptance-form-field">
                      <label className="ail-acceptance-field-label">Acceptance Date</label>
                      <input
                        type="date"
                        className="ail-acceptance-input"
                        value={acceptanceForm.acceptanceDate}
                        onChange={(e) => handleAcceptanceFormChange('acceptanceDate', e.target.value)}
                      />
                    </div>
                    <div className="ail-acceptance-form-field">
                      <label className="ail-acceptance-field-label">Payment Commitment Date</label>
                      <input
                        type="date"
                        className="ail-acceptance-input"
                        placeholder="dd-mm-yyyy"
                        value={acceptanceForm.paymentCommitmentDate}
                        onChange={(e) => handleAcceptanceFormChange('paymentCommitmentDate', e.target.value)}
                      />
                    </div>
                  </div>
                  
                  {/* Row 3: Acceptance Notes */}
                  <div className="ail-acceptance-form-field">
                    <label className="ail-acceptance-field-label">Acceptance Notes</label>
                    <textarea
                      className="ail-acceptance-textarea"
                      placeholder="Any additional comments or payment instructions..."
                      rows="2"
                      value={acceptanceForm.acceptanceNotes}
                      onChange={(e) => handleAcceptanceFormChange('acceptanceNotes', e.target.value)}
                    />
                  </div>
                </div>
              )}

              {/* Form Fields for Dispute */}
              {acceptanceForm.decision === 'DISPUTE' && (
                <div className="ail-acceptance-form-fields">
                  {/* Row 1: Accepted By and Designation */}
                  <div className="ail-acceptance-form-row">
                    <div className="ail-acceptance-form-field">
                      <label className="ail-acceptance-field-label">
                        Accepted By (Name) <span className="ail-required">*</span>
                      </label>
                      <input
                        type="text"
                        className="ail-acceptance-input"
                        placeholder="Your full name"
                        value={acceptanceForm.acceptedBy}
                        onChange={(e) => handleAcceptanceFormChange('acceptedBy', e.target.value)}
                      />
                    </div>
                    <div className="ail-acceptance-form-field">
                      <label className="ail-acceptance-field-label">
                        Designation <span className="ail-required">*</span>
                      </label>
                      <input
                        type="text"
                        className="ail-acceptance-input"
                        placeholder="e.g., Finance Manager"
                        value={acceptanceForm.designation}
                        onChange={(e) => handleAcceptanceFormChange('designation', e.target.value)}
                      />
                    </div>
                  </div>
                  
                  {/* Row 2: Acceptance Date */}
                  <div className="ail-acceptance-form-field">
                    <label className="ail-acceptance-field-label">Acceptance Date</label>
                    <input
                      type="date"
                      className="ail-acceptance-input"
                      value={acceptanceForm.acceptanceDate}
                      onChange={(e) => handleAcceptanceFormChange('acceptanceDate', e.target.value)}
                    />
                  </div>
                  
                  {/* Row 3: Dispute Category */}
                  <div className="ail-acceptance-form-field">
                    <label className="ail-acceptance-field-label">
                      Dispute Category <span className="ail-required">*</span>
                    </label>
                    <select
                      className="ail-acceptance-input"
                      value={acceptanceForm.disputeCategory}
                      onChange={(e) => handleAcceptanceFormChange('disputeCategory', e.target.value)}
                    >
                      <option value="">Select dispute category...</option>
                      <option value="Pricing Error">Pricing Error</option>
                      <option value="Quantity Mismatch">Quantity Mismatch</option>
                      <option value="Quality Issue">Quality Issue</option>
                      <option value="Delivery Issue">Delivery Issue</option>
                      <option value="Tax Calculation Error">Tax Calculation Error</option>
                      <option value="Terms & Conditions">Terms & Conditions</option>
                      <option value="Documentation Issue">Documentation Issue</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>
                  
                  {/* Row 4: Dispute Reason */}
                  <div className="ail-acceptance-form-field">
                    <label className="ail-acceptance-field-label">
                      Dispute Reason <span className="ail-required">*</span>
                    </label>
                    <textarea
                      className="ail-acceptance-textarea"
                      placeholder="Explain the reason for disputing this invoice..."
                      rows="3"
                      value={acceptanceForm.disputeReason}
                      onChange={(e) => handleAcceptanceFormChange('disputeReason', e.target.value)}
                    />
                  </div>
                </div>
              )}

              {/* Additional Remarks */}
              <div className="ail-acceptance-form-field">
                <label className="ail-acceptance-field-label">Additional Remarks</label>
                <textarea
                  className="ail-acceptance-textarea"
                  placeholder="Any other information..."
                  rows="2"
                  value={acceptanceForm.remarks}
                  onChange={(e) => handleAcceptanceFormChange('remarks', e.target.value)}
                />
              </div>
            </div>

            <div className="ail-modal-footer">
              <button 
                className="ail-btn ail-btn-secondary"
                onClick={handleCloseAcceptanceModal}
              >
                Cancel
              </button>
              <button 
                className="ail-btn ail-btn-primary"
                onClick={handleSubmitDecision}
                disabled={
                  !acceptanceForm.decision || 
                  (acceptanceForm.decision === 'ACCEPT' && (!acceptanceForm.acceptedBy || !acceptanceForm.designation)) ||
                  (acceptanceForm.decision === 'DISPUTE' && (!acceptanceForm.acceptedBy || !acceptanceForm.designation || !acceptanceForm.disputeCategory || !acceptanceForm.disputeReason))
                }
              >
                Submit Decision
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

